/**
 * URLs the pack must capture that only the CLIENT can spell.
 *
 * WHY THIS FILE EXISTS
 *
 * Most of what a pack captures is declared server-side, in
 * `backend/offline_pack/registry.py`, because the shapes are literals the
 * server can write down: `?page=1&page_size=25`, `?commodity_type=solid`,
 * `?show_archived=false`.
 *
 * A few are not. The Trade Ops dashboards filter by year using
 *
 *     new Date(year, 0, 1).toISOString()
 *
 * which is LOCAL midnight rendered as UTC — so in Johannesburg the request for
 * 2026 actually carries `2025-12-31T22:00:00.000Z`. The server cannot predict
 * that string: it depends on the timezone of the laptop, which the server has
 * never been told. Any server-side guess is wrong by two hours for most of our
 * users, and a cache key that is wrong by two hours is simply a miss — which
 * is precisely how Trade Ops came to show "No Deliveries Yet" offline while
 * three deliveries sat in IndexedDB (2026-08-11).
 *
 * So these are captured by the browser instead, using THE SAME helper the
 * screen itself uses to build its request. The value cannot diverge from what
 * the screen will later ask for, because there is only one thing computing it.
 * That is the whole design: not "keep the two in sync", but "make there be one".
 *
 * The capture goes through the ordinary authenticated endpoint, so every
 * permission class and row-level scope applies exactly as it does online — a
 * 403 here is still the access gate, same as a 403 inside the builder.
 *
 * PREMISE: every entry below is a plain GET, side-effect free, answerable from
 * our own database. Do NOT add anything that reaches NavAPI, Kpler, DocuWare or
 * the GPU service — `/api/globe/resolve-port/` looks local and is not (it falls
 * through to a billed SeaPortSearch), and bulk-querying NavAPI is what got our
 * key suspended in July 2026. If you want an external endpoint offline, that
 * argument belongs in registry.py's docstring, not here.
 */

import { tradeOpsYearRange } from '../utils/tradeOpsDateRange';

/**
 * Commodity dashboards that exist, spelled EXACTLY as the routes spell them.
 *
 * `solids`, plural. App.js:113 renders
 * `<TradeOperationsLayout commodityType="solids" />` and the value goes onto
 * the wire verbatim. `solid` (singular) is a different string and therefore a
 * different cache key and therefore a miss — which is the entire bug this file
 * exists to fix, so getting it wrong here would be quite the own goal.
 */
const COMMODITIES = ['solids', 'fuels'];

/**
 * The list endpoints that take the same year+commodity filter.
 *
 * Bunkers has no Bid/Offer tab, so `user-offers` is solids-only — mirroring
 * `TradeOperationsLayout.fetchData`, which skips that call for fuels.
 * Legal contracts are here rather than in the server registry for the same
 * reason as the rest: `legalContractService` filters on the same
 * browser-computed ISO year boundaries.
 */
const TRADE_OPS_ENDPOINTS = [
  { url: '/api/offer/trade-recaps/', commodities: COMMODITIES },
  { url: '/api/offer/freight-recaps/', commodities: COMMODITIES },
  { url: '/api/deliveries/', commodities: COMMODITIES },
  { url: '/api/offer/user-offers/', commodities: ['solids'] },
  // Legal has three tabs but only Bunkers is enabled (LegalDashboard.js:35-57);
  // Cargo and Freight are explicitly "arrive in a later phase". Capturing the
  // two disabled ones would be two wasted requests per pack forever.
  { url: '/api/legal/contracts/', commodities: ['fuels'] },
];

/**
 * Years to capture, relative to now.
 *
 * The dashboard opens on the current year and has arrows either side. Capturing
 * last year as well is four extra requests and covers both the "check what we
 * did last year" case and the few hours around New Year when a laptop's local
 * date and the server's may disagree about which year "this year" is.
 */
const YEAR_OFFSETS = [0, -1];

/**
 * Everything the browser should capture itself, as `{ url, params, label }`.
 *
 * `now` is injectable so tests can pin a date without waiting for January.
 */
export function clientVariantRequests(now = new Date()) {
  const requests = [];

  YEAR_OFFSETS.forEach((offset) => {
    const year = now.getFullYear() + offset;
    const range = tradeOpsYearRange(new Date(year, 5, 1));

    TRADE_OPS_ENDPOINTS.forEach(({ url, commodities }) => {
      commodities.forEach((commodity) => {
        requests.push({
          url,
          params: {
            start_date: range.startDate,
            end_date: range.endDate,
            commodity_type: commodity,
          },
          label: `${url} (${commodity}, ${year})`,
        });
      });
    });
  });

  return requests;
}

export default clientVariantRequests;
